import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Printer, ShieldAlert, Star, Trash2 } from 'lucide-react';
import { Button } from '@qargo-ims/ui';
import { apiFetch } from '@/lib/api/client';
import { useApi } from '@/lib/api/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatShortDate } from '@/lib/format';

type ProfileTab = 'profile' | 'pay' | 'warnings' | 'reviews';

interface Barista {
  id: string;
  username: string;
  displayName: string | null;
  role: 'barista' | 'shift_lead';
  status: 'active' | 'disabled' | 'terminated';
  homeStoreId: string;
  homeStoreCode: string;
  homeStoreName: string;
  createdAt: string;
  disabledAt: string | null;
}
interface HrProfile {
  hiringNotes: string | null;
  payRateCents: number | null;
  payNotes: string | null;
  employmentType: string | null;
  startDate: string | null;
  endDate: string | null;
  updatedAt: string;
  updatedByEntraOid: string | null;
}
interface ProfileResponse {
  barista: Barista;
  profile: HrProfile | null;
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

const EMPLOYMENT_TYPES = [
  ['', '—'],
  ['full_time', 'Full-time'],
  ['part_time', 'Part-time'],
  ['contractor', 'Contractor'],
  ['seasonal', 'Seasonal'],
] as const;

interface ProfileForm {
  hiringNotes: string;
  employmentType: string;
  startDate: string;
  endDate: string;
  payRateDollars: string;
  payNotes: string;
}

function formFromProfile(p: HrProfile | null): ProfileForm {
  return {
    hiringNotes: p?.hiringNotes ?? '',
    employmentType: p?.employmentType ?? '',
    startDate: p?.startDate ?? '',
    endDate: p?.endDate ?? '',
    payRateDollars: p?.payRateCents != null ? (p.payRateCents / 100).toFixed(2) : '',
    payNotes: p?.payNotes ?? '',
  };
}

function dateLabel(s: string | null): string {
  if (!s) return '—';
  return new Date(`${s}T00:00:00`).toLocaleDateString();
}

export function AdminUserDetail() {
  const { id = '' } = useParams();
  const { data, loading, error, refetch } = useApi<ProfileResponse>(
    id ? `/users/${id}/hr-profile` : null,
  );
  const barista = data?.barista;
  const [tab, setTab] = useState<ProfileTab>('profile');

  const [form, setForm] = useState<ProfileForm>(formFromProfile(null));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (data) setForm(formFromProfile(data.profile));
  }, [data]);

  function set<K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setMsg(null);
  }

  async function save() {
    setMsg(null);
    let payRateCents: number | null = null;
    const raw = form.payRateDollars.trim();
    if (raw !== '') {
      const dollars = Number(raw);
      if (!Number.isFinite(dollars) || dollars < 0) {
        setMsg('Pay rate must be a non-negative number.');
        return;
      }
      payRateCents = Math.round(dollars * 100);
    }
    setSaving(true);
    try {
      await apiFetch(`/users/${id}/hr-profile`, {
        method: 'PUT',
        body: JSON.stringify({
          hiringNotes: form.hiringNotes.trim() || null,
          employmentType: form.employmentType || null,
          startDate: form.startDate || null,
          endDate: form.endDate || null,
          payRateCents,
          payNotes: form.payNotes.trim() || null,
        }),
      });
      setMsg('Saved');
      refetch();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading || !barista) {
    return (
      <div className="space-y-6">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden /> Users
        </Link>
        {error ? (
          <p className="text-sm text-destructive">Could not load HR profile: {error}</p>
        ) : (
          <Skeleton className="h-64 w-full rounded-xl" />
        )}
      </div>
    );
  }

  const savedAt = data?.profile?.updatedAt;

  return (
    <div className="space-y-6">
      <Link
        to="/admin/users"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden /> Users
      </Link>
      <PageHeader
        title={barista.displayName ?? barista.username}
        description={`@${barista.username} · ${barista.role === 'shift_lead' ? 'Shift lead' : 'Barista'} · ${barista.homeStoreCode} ${barista.homeStoreName}`}
        actions={
          <Badge variant={barista.status === 'active' ? 'success' : 'muted'}>
            {barista.status}
          </Badge>
        }
      />

      <div className="flex gap-1 border-b border-border">
        {(
          [
            ['profile', 'Profile'],
            ['pay', 'Pay'],
            ['warnings', 'Warnings'],
            ['reviews', 'Reviews'],
          ] as [ProfileTab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' ? (
        <Card>
          <CardHeader>
            <CardTitle>Employment</CardTitle>
            <p className="text-sm text-muted-foreground">
              Hiring notes and employment details. Visible to HR, Admins, and C-level only.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Employment type</label>
                <select
                  className={inputClass}
                  value={form.employmentType}
                  onChange={(e) => set('employmentType', e.target.value)}
                >
                  {EMPLOYMENT_TYPES.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Start date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.startDate}
                  onChange={(e) => set('startDate', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">End date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.endDate}
                  onChange={(e) => set('endDate', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Hiring notes</label>
              <textarea
                className={inputClass}
                rows={5}
                value={form.hiringNotes}
                placeholder="Interview notes, references, onboarding context…"
                onChange={(e) => set('hiringNotes', e.target.value)}
              />
            </div>
            <SaveRow saving={saving} msg={msg} onSave={save} savedAt={savedAt} />
          </CardContent>
        </Card>
      ) : null}

      {tab === 'pay' ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="size-4 text-amber-600" aria-hidden />
              <CardTitle>Pay information</CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              Sensitive PII — read and write are restricted to HR, Admins, and C-level (enforced by
              the API).
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Pay rate (USD)</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    className={cn(inputClass, 'pl-6')}
                    value={form.payRateDollars}
                    placeholder="0.00"
                    onChange={(e) => set('payRateDollars', e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Hourly or per-period — note which below.
                </p>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Pay notes</label>
              <textarea
                className={inputClass}
                rows={4}
                value={form.payNotes}
                placeholder="Rate basis, raises, bonus structure, last review…"
                onChange={(e) => set('payNotes', e.target.value)}
              />
            </div>
            <SaveRow saving={saving} msg={msg} onSave={save} savedAt={savedAt} />
          </CardContent>
        </Card>
      ) : null}

      {tab === 'warnings' ? <WarningsTab barista={barista} /> : null}
      {tab === 'reviews' ? <ReviewsTab barista={barista} /> : null}
    </div>
  );
}

function SaveRow({
  saving,
  msg,
  onSave,
  savedAt,
}: {
  saving: boolean;
  msg: string | null;
  onSave: () => void;
  savedAt?: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 pt-1">
      <Button onClick={onSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
      {msg ? (
        <span className={msg === 'Saved' ? 'text-sm text-success' : 'text-sm text-destructive'}>
          {msg}
        </span>
      ) : savedAt ? (
        <span className="text-xs text-muted-foreground">
          Last updated {formatShortDate(savedAt)}
        </span>
      ) : null}
    </div>
  );
}

// ---- Warnings ---------------------------------------------------------------

interface Warning {
  id: string;
  type: 'verbal' | 'written';
  reason: string;
  writeupNotes: string | null;
  talkingPoints: string | null;
  outcome: string | null;
  warningDate: string | null;
  createdAt: string;
  updatedAt: string;
  printCount: number;
  lastPrintedAt: string | null;
  issuedBy: { entraOid: string; displayName: string | null; email: string | null };
  updatedBy: { entraOid: string; displayName: string | null } | null;
  hrCopy: { ticketId: string; status: string; acknowledgedAt: string | null } | null;
}

const WARNING_TYPE_LABEL: Record<string, string> = {
  verbal: 'Verbal warning',
  written: 'Written warning',
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Open a Qargo/TMKN-branded printable warning notice in a new window + print. */
function printWarningDoc(barista: Barista, w: Warning) {
  const issuer = w.issuedBy.displayName ?? w.issuedBy.email ?? 'Manager';
  const employee = barista.displayName ?? barista.username;
  const block = (title: string, body: string | null) =>
    body
      ? `<div class="block"><div class="label">${escapeHtml(title)}</div><div class="body">${escapeHtml(
          body,
        ).replace(/\n/g, '<br>')}</div></div>`
      : '';
  const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Employee Warning Notice — ${escapeHtml(employee)}</title>
<style>
  :root { --espresso:#3a2e26; --caramel:#a5703f; }
  * { box-sizing: border-box; }
  body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color:#1c1714; margin:0; padding:0; }
  .page { max-width: 760px; margin: 0 auto; padding: 40px 48px; }
  .head { display:flex; align-items:center; justify-content:space-between; border-bottom:4px solid var(--caramel); padding-bottom:16px; }
  .brand { font-size:22px; font-weight:800; letter-spacing:0.04em; color:var(--espresso); }
  .brand small { display:block; font-size:11px; font-weight:600; letter-spacing:0.14em; color:var(--caramel); margin-top:2px; }
  .doctype { text-align:right; font-size:13px; color:#6b5d52; }
  .doctype strong { display:block; font-size:16px; color:var(--espresso); text-transform:uppercase; letter-spacing:0.06em; }
  .meta { display:grid; grid-template-columns:1fr 1fr; gap:6px 24px; margin:24px 0; font-size:13px; }
  .meta div span { color:#6b5d52; }
  .typebar { background:var(--espresso); color:#fff; padding:8px 14px; border-radius:6px; font-weight:700; letter-spacing:0.03em; display:inline-block; }
  .block { margin:18px 0; }
  .label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.08em; color:var(--caramel); margin-bottom:4px; }
  .body { font-size:14px; line-height:1.5; white-space:normal; }
  .sigs { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:56px; }
  .sig { border-top:1px solid #8a7c70; padding-top:6px; font-size:12px; color:#6b5d52; }
  .foot { margin-top:40px; padding-top:12px; border-top:1px solid #e4ddd6; font-size:10px; color:#9a8d82; }
  @media print { .page { padding: 24px; } }
</style></head>
<body><div class="page">
  <div class="head">
    <div class="brand">QARGO COFFEE<small>TMKN OPERATIONS</small></div>
    <div class="doctype"><strong>Employee Warning Notice</strong>Confidential — HR record</div>
  </div>
  <div class="meta">
    <div><span>Employee:</span> <strong>${escapeHtml(employee)}</strong></div>
    <div><span>Username:</span> @${escapeHtml(barista.username)}</div>
    <div><span>Location:</span> ${escapeHtml(barista.homeStoreCode)} — ${escapeHtml(barista.homeStoreName)}</div>
    <div><span>Date:</span> ${escapeHtml(dateLabel(w.warningDate))}</div>
    <div><span>Issued by:</span> ${escapeHtml(issuer)}</div>
  </div>
  <div class="typebar">${escapeHtml(WARNING_TYPE_LABEL[w.type] ?? w.type)}</div>
  ${block('Reason', w.reason)}
  ${block('Write-up', w.writeupNotes)}
  ${block('Talking points', w.talkingPoints)}
  ${block('Outcome / corrective action', w.outcome)}
  <div class="sigs">
    <div class="sig">Employee signature &nbsp; · &nbsp; Date</div>
    <div class="sig">Manager signature &nbsp; · &nbsp; Date</div>
  </div>
  <div class="foot">Qargo Coffee · TMKN Operations — this notice is a confidential HR record. Generated from Qargo IMS.</div>
</div>
<script>window.onload = function(){ window.print(); }</script>
</body></html>`;
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

function WarningsTab({ barista }: { barista: Barista }) {
  const baristaId = barista.id;
  const [warnings, setWarnings] = useState<Warning[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<
    { mode: 'create' } | { mode: 'edit'; warning: Warning } | null
  >(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch<{ warnings: Warning[] }>(`/users/${baristaId}/warnings`);
      setWarnings(r.warnings);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [baristaId]);

  async function onPrint(w: Warning) {
    printWarningDoc(barista, w);
    try {
      await apiFetch(`/users/${baristaId}/warnings/${w.id}/printed`, { method: 'POST' });
      await reload();
    } catch {
      // print already happened; the trail update is best-effort
    }
  }

  async function remove(w: Warning) {
    if (!window.confirm(`Delete this ${w.type} warning? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/users/${baristaId}/warnings/${w.id}`, { method: 'DELETE' });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Warnings</CardTitle>
          <Button onClick={() => setDialog({ mode: 'create' })}>
            <Plus className="size-4" aria-hidden /> New warning
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Verbal and written write-ups. Edit to update the outcome after the conversation, then
          re-print. Each is stamped with the issuer and an edit + print trail.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : !warnings || warnings.length === 0 ? (
          <EmptyState
            icon={ShieldAlert}
            title="No warnings on record"
            description="Create a verbal or written warning above."
          />
        ) : (
          <ul className="space-y-3">
            {warnings.map((w) => (
              <li key={w.id} className="rounded-lg border border-border p-4">
                <div className="flex items-start gap-3">
                  <Badge variant={w.type === 'written' ? 'destructive' : 'warning'}>{w.type}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{w.reason}</p>
                    {w.writeupNotes ? (
                      <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                        {w.writeupNotes}
                      </p>
                    ) : null}
                    {w.talkingPoints ? (
                      <p className="mt-2 text-sm">
                        <span className="font-medium">Talking points: </span>
                        <span className="whitespace-pre-wrap text-muted-foreground">
                          {w.talkingPoints}
                        </span>
                      </p>
                    ) : null}
                    <p className="mt-2 text-sm">
                      <span className="font-medium">Outcome: </span>
                      {w.outcome ? (
                        <span className="whitespace-pre-wrap text-muted-foreground">
                          {w.outcome}
                        </span>
                      ) : (
                        <span className="italic text-amber-700">pending — edit to add</span>
                      )}
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {dateLabel(w.warningDate)} · issued by{' '}
                      {w.issuedBy.displayName ?? w.issuedBy.email ?? 'a manager'}
                      {w.updatedBy
                        ? ` · edited by ${w.updatedBy.displayName ?? 'a manager'} ${formatShortDate(w.updatedAt)}`
                        : ''}
                      {w.printCount > 0
                        ? ` · printed ${w.printCount}× (last ${formatShortDate(w.lastPrintedAt)})`
                        : ''}
                    </p>
                    {w.hrCopy ? (
                      <p className="mt-1">
                        <Badge variant={w.hrCopy.status === 'resolved' ? 'success' : 'warning'}>
                          {w.hrCopy.status === 'resolved'
                            ? `HR acknowledged ${formatShortDate(w.hrCopy.acknowledgedAt)}`
                            : 'Copied to HR · awaiting acknowledgment'}
                        </Badge>
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <button
                      type="button"
                      onClick={() => void onPrint(w)}
                      className="inline-flex items-center gap-1 rounded-md p-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Printer className="size-4" aria-hidden /> Print
                    </button>
                    <button
                      type="button"
                      onClick={() => setDialog({ mode: 'edit', warning: w })}
                      className="inline-flex items-center gap-1 rounded-md p-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Pencil className="size-4" aria-hidden /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(w)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-md p-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-destructive"
                    >
                      <Trash2 className="size-4" aria-hidden /> Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {dialog ? (
        <WarningDialog
          baristaId={baristaId}
          warning={dialog.mode === 'edit' ? dialog.warning : null}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void reload();
          }}
        />
      ) : null}
    </Card>
  );
}

function WarningDialog({
  baristaId,
  warning,
  onClose,
  onSaved,
}: {
  baristaId: string;
  warning: Warning | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = warning != null;
  const [type, setType] = useState<'verbal' | 'written'>(warning?.type ?? 'verbal');
  const [warningDate, setWarningDate] = useState(
    warning?.warningDate ?? new Date().toISOString().slice(0, 10),
  );
  const [reason, setReason] = useState(warning?.reason ?? '');
  const [writeupNotes, setWriteupNotes] = useState(warning?.writeupNotes ?? '');
  const [talkingPoints, setTalkingPoints] = useState(warning?.talkingPoints ?? '');
  const [outcome, setOutcome] = useState(warning?.outcome ?? '');
  const [sendToHr, setSendToHr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!reason.trim()) {
      setErr('A reason is required.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    const payload = {
      type,
      warningDate,
      reason: reason.trim(),
      writeupNotes: writeupNotes.trim() || null,
      talkingPoints: talkingPoints.trim() || null,
      outcome: outcome.trim() || null,
    };
    try {
      if (editing) {
        await apiFetch(`/users/${baristaId}/warnings/${warning.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch(`/users/${baristaId}/warnings`, {
          method: 'POST',
          body: JSON.stringify({ ...payload, sendToHr }),
        });
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-6">
      <div className="my-8 w-full max-w-lg space-y-3 rounded-xl bg-card p-6">
        <h3 className="text-lg font-semibold">{editing ? 'Edit warning' : 'New warning'}</h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            Type
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'verbal' | 'written')}
              className={`mt-1 ${inputClass}`}
            >
              <option value="verbal">Verbal</option>
              <option value="written">Written</option>
            </select>
          </label>
          <label className="block text-sm">
            Date
            <input
              type="date"
              value={warningDate}
              onChange={(e) => setWarningDate(e.target.value)}
              className={`mt-1 ${inputClass}`}
            />
          </label>
        </div>
        <label className="block text-sm">
          Reason
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={256}
            className={`mt-1 ${inputClass}`}
            placeholder="Short reason (e.g. repeated lateness)"
            autoFocus
          />
        </label>
        <label className="block text-sm">
          Write-up notes
          <textarea
            value={writeupNotes}
            onChange={(e) => setWriteupNotes(e.target.value)}
            rows={4}
            className={`mt-1 ${inputClass}`}
            placeholder="Detailed description of the issue and prior context…"
          />
        </label>
        <label className="block text-sm">
          Talking points
          <textarea
            value={talkingPoints}
            onChange={(e) => setTalkingPoints(e.target.value)}
            rows={3}
            className={`mt-1 ${inputClass}`}
            placeholder="What to cover in the conversation…"
          />
        </label>
        <label className="block text-sm">
          Outcome / corrective action
          <textarea
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            rows={3}
            className={`mt-1 ${inputClass}`}
            placeholder="Often filled after the conversation — you can edit and re-print later."
          />
        </label>
        {!editing ? (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={sendToHr}
              onChange={(e) => setSendToHr(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Send a copy to HR — posts to the HR Teams channel and emails hr@tmkngroup.com, and
              opens a ticket for HR to acknowledge.
            </span>
          </label>
        ) : null}
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : editing ? 'Save changes' : 'Create warning'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Performance reviews ----------------------------------------------------

type Rating = 'exceeds' | 'meets' | 'needs_improvement' | 'unsatisfactory';

const RATINGS: [Rating | '', string][] = [
  ['', '—'],
  ['exceeds', 'Exceeds'],
  ['meets', 'Meets'],
  ['needs_improvement', 'Needs improvement'],
  ['unsatisfactory', 'Unsatisfactory'],
];

const REVIEW_CATEGORIES: [string, string][] = [
  ['punctuality', 'Punctuality'],
  ['quality_of_work', 'Quality of work'],
  ['teamwork', 'Teamwork'],
  ['reliability', 'Reliability'],
  ['customer_service', 'Customer service'],
];

function ratingVariant(r: string | null | undefined): NonNullable<BadgeProps['variant']> {
  switch (r) {
    case 'exceeds':
      return 'success';
    case 'meets':
      return 'secondary';
    case 'needs_improvement':
      return 'warning';
    case 'unsatisfactory':
      return 'destructive';
    default:
      return 'muted';
  }
}
function ratingLabel(r: string | null | undefined): string {
  return RATINGS.find(([v]) => v === r)?.[1] ?? '—';
}

interface Review {
  id: string;
  reviewDate: string | null;
  period: string | null;
  overallRating: string | null;
  categoryRatings: Record<string, string> | null;
  summary: string | null;
  goals: string | null;
  nextReviewDate: string | null;
  acknowledgedAt: string | null;
  acknowledgedNote: string | null;
  createdAt: string;
  updatedAt: string;
  reviewer: { entraOid: string; displayName: string | null; email: string | null };
  updatedBy: { entraOid: string; displayName: string | null } | null;
}

function ReviewsTab({ barista }: { barista: Barista }) {
  const baristaId = barista.id;
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<
    { mode: 'create' } | { mode: 'edit'; review: Review } | null
  >(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch<{ reviews: Review[] }>(`/users/${baristaId}/performance-reviews`);
      setReviews(r.reviews);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [baristaId]);

  async function remove(r: Review) {
    if (!window.confirm('Delete this performance review? This cannot be undone.')) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/users/${baristaId}/performance-reviews/${r.id}`, { method: 'DELETE' });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Performance reviews</CardTitle>
          <Button onClick={() => setDialog({ mode: 'create' })}>
            <Plus className="size-4" aria-hidden /> New review
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Structured reviews with category ratings, goals, and employee acknowledgment. Editable +
          audited.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : !reviews || reviews.length === 0 ? (
          <EmptyState
            icon={Star}
            title="No reviews yet"
            description="Record a performance review for this employee."
          />
        ) : (
          <ul className="space-y-3">
            {reviews.map((r) => (
              <li key={r.id} className="rounded-lg border border-border p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">{dateLabel(r.reviewDate)}</span>
                      {r.period ? (
                        <span className="text-xs text-muted-foreground">· {r.period}</span>
                      ) : null}
                      <Badge variant={ratingVariant(r.overallRating)}>
                        Overall: {ratingLabel(r.overallRating)}
                      </Badge>
                      {r.acknowledgedAt ? (
                        <Badge variant="success">Acknowledged {dateLabel(r.acknowledgedAt)}</Badge>
                      ) : (
                        <Badge variant="muted">Not acknowledged</Badge>
                      )}
                    </div>
                    {r.categoryRatings && Object.keys(r.categoryRatings).length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {REVIEW_CATEGORIES.filter(([key]) => r.categoryRatings?.[key]).map(
                          ([key, label]) => (
                            <span
                              key={key}
                              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs"
                            >
                              {label}:{' '}
                              <span className="font-medium">
                                {ratingLabel(r.categoryRatings?.[key])}
                              </span>
                            </span>
                          ),
                        )}
                      </div>
                    ) : null}
                    {r.summary ? (
                      <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                        {r.summary}
                      </p>
                    ) : null}
                    {r.goals ? (
                      <p className="mt-2 text-sm">
                        <span className="font-medium">Goals: </span>
                        <span className="whitespace-pre-wrap text-muted-foreground">{r.goals}</span>
                      </p>
                    ) : null}
                    <p className="mt-2 text-xs text-muted-foreground">
                      reviewer {r.reviewer.displayName ?? r.reviewer.email ?? 'a manager'}
                      {r.nextReviewDate ? ` · next review ${dateLabel(r.nextReviewDate)}` : ''}
                      {r.updatedBy
                        ? ` · edited by ${r.updatedBy.displayName ?? 'a manager'} ${formatShortDate(r.updatedAt)}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <button
                      type="button"
                      onClick={() => setDialog({ mode: 'edit', review: r })}
                      className="inline-flex items-center gap-1 rounded-md p-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Pencil className="size-4" aria-hidden /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(r)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-md p-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-destructive"
                    >
                      <Trash2 className="size-4" aria-hidden /> Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {dialog ? (
        <ReviewDialog
          baristaId={baristaId}
          review={dialog.mode === 'edit' ? dialog.review : null}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void reload();
          }}
        />
      ) : null}
    </Card>
  );
}

function ReviewDialog({
  baristaId,
  review,
  onClose,
  onSaved,
}: {
  baristaId: string;
  review: Review | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = review != null;
  const [reviewDate, setReviewDate] = useState(
    review?.reviewDate ?? new Date().toISOString().slice(0, 10),
  );
  const [period, setPeriod] = useState(review?.period ?? '');
  const [overallRating, setOverallRating] = useState<string>(review?.overallRating ?? '');
  const [cats, setCats] = useState<Record<string, string>>(review?.categoryRatings ?? {});
  const [summary, setSummary] = useState(review?.summary ?? '');
  const [goals, setGoals] = useState(review?.goals ?? '');
  const [nextReviewDate, setNextReviewDate] = useState(review?.nextReviewDate ?? '');
  const [acknowledgedAt, setAcknowledgedAt] = useState(review?.acknowledgedAt ?? '');
  const [acknowledgedNote, setAcknowledgedNote] = useState(review?.acknowledgedNote ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function setCat(key: string, value: string) {
    setCats((c) => {
      const next = { ...c };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  }

  async function submit() {
    if (!reviewDate) {
      setErr('Review date is required.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    const payload = {
      reviewDate,
      period: period.trim() || null,
      overallRating: overallRating || null,
      categoryRatings: Object.keys(cats).length > 0 ? cats : null,
      summary: summary.trim() || null,
      goals: goals.trim() || null,
      nextReviewDate: nextReviewDate || null,
      acknowledgedAt: acknowledgedAt || null,
      acknowledgedNote: acknowledgedNote.trim() || null,
    };
    try {
      if (editing) {
        await apiFetch(`/users/${baristaId}/performance-reviews/${review.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch(`/users/${baristaId}/performance-reviews`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-6">
      <div className="my-8 w-full max-w-2xl space-y-3 rounded-xl bg-card p-6">
        <h3 className="text-lg font-semibold">
          {editing ? 'Edit review' : 'New performance review'}
        </h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block text-sm">
            Review date
            <input
              type="date"
              value={reviewDate}
              onChange={(e) => setReviewDate(e.target.value)}
              className={`mt-1 ${inputClass}`}
            />
          </label>
          <label className="block text-sm">
            Period
            <input
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              maxLength={64}
              placeholder="e.g. 2026 Annual"
              className={`mt-1 ${inputClass}`}
            />
          </label>
          <label className="block text-sm">
            Overall rating
            <select
              value={overallRating}
              onChange={(e) => setOverallRating(e.target.value)}
              className={`mt-1 ${inputClass}`}
            >
              {RATINGS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-muted-foreground">Category ratings</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {REVIEW_CATEGORIES.map(([key, label]) => (
              <label key={key} className="flex items-center justify-between gap-2 text-sm">
                <span>{label}</span>
                <select
                  value={cats[key] ?? ''}
                  onChange={(e) => setCat(key, e.target.value)}
                  className={`${inputClass} max-w-[12rem]`}
                >
                  {RATINGS.map(([value, rlabel]) => (
                    <option key={value} value={value}>
                      {rlabel}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </div>

        <label className="block text-sm">
          Summary
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            className={`mt-1 ${inputClass}`}
            placeholder="Overall assessment…"
          />
        </label>
        <label className="block text-sm">
          Goals
          <textarea
            value={goals}
            onChange={(e) => setGoals(e.target.value)}
            rows={2}
            className={`mt-1 ${inputClass}`}
            placeholder="Goals for the next period…"
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            Next review date
            <input
              type="date"
              value={nextReviewDate}
              onChange={(e) => setNextReviewDate(e.target.value)}
              className={`mt-1 ${inputClass}`}
            />
          </label>
          <label className="block text-sm">
            Employee acknowledged on
            <input
              type="date"
              value={acknowledgedAt}
              onChange={(e) => setAcknowledgedAt(e.target.value)}
              className={`mt-1 ${inputClass}`}
            />
          </label>
        </div>
        <label className="block text-sm">
          Acknowledgment note
          <input
            value={acknowledgedNote}
            onChange={(e) => setAcknowledgedNote(e.target.value)}
            maxLength={256}
            placeholder="e.g. Reviewed in person; employee agrees with goals"
            className={`mt-1 ${inputClass}`}
          />
        </label>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : editing ? 'Save changes' : 'Create review'}
          </Button>
        </div>
      </div>
    </div>
  );
}
